// src/components/MessageInput.js
/**
 * Clean input with send, location share, and offer buttons
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Tag, Camera } from 'lucide-react';

export default function MessageInput({
  onSend,
  onOffer,
  onTypingChange,
  onAttachImage,
  disabled = false,
}) {
  const { t } = useTranslation(['inbox', 'common']);
  const [text, setText] = useState('');
  const typingRef = useRef(false);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  function handleTyping(val) {
    setText(val);
    if (!onTypingChange) return;
    if (!typingRef.current) {
      typingRef.current = true;
      onTypingChange(true);
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      typingRef.current = false;
      onTypingChange(false);
    }, 1200);
  }

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    await onSend(trimmed);
    setText('');
    if (typingRef.current && onTypingChange) {
      typingRef.current = false;
      onTypingChange(false);
    }
    inputRef.current?.focus();
  }

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const hasText = text.trim().length > 0;

  return (
    <div className="flex items-end gap-2 px-3 pt-2.5 pb-2 sm:px-4 sm:py-3">
      {/* Action buttons row (left side) */}
      <div className="flex items-center gap-1 flex-shrink-0 pb-0.5">
        {onAttachImage && (
          <button
            type="button"
            onClick={onAttachImage}
            className="w-10 h-10 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
            disabled={disabled}
            title={t('inbox:attachPhoto', 'Attach photo')}
          >
            <Camera className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Text input */}
      <div className="flex-1 relative">
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => handleTyping(e.target.value)}
          onKeyDown={onKey}
          placeholder={t('inbox:writeMessage', 'Write a message here')}
          className="w-full resize-none rounded-2xl border border-border bg-muted/50 text-foreground px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500/50 transition-all text-sm leading-5 max-h-36 placeholder-muted-foreground"
          rows={1}
          disabled={disabled}
          style={{
            minHeight: '44px',
            height: 'auto',
            overflow: text.split('\n').length > 3 ? 'auto' : 'hidden',
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 144) + 'px';
          }}
        />
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-1 flex-shrink-0 pb-0.5">
        {onOffer && !hasText && (
          <button
            type="button"
            onClick={onOffer}
            className="w-10 h-10 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-40"
            disabled={disabled}
            title={t('inbox:makeOffer', 'Make an offer')}
          >
            <Tag className="w-5 h-5" />
          </button>
        )}
        <button
          type="button"
          onClick={handleSend}
          disabled={!hasText || disabled}
          className={`w-11 h-11 inline-flex items-center justify-center rounded-full transition-all duration-200 ${
            hasText
              ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm'
              : 'text-muted-foreground bg-transparent'
          } disabled:opacity-30 disabled:cursor-not-allowed`}
          title={t('inbox:send', 'Send')}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
